// ── VPS Settings / Architecture Tab ─────────────────────────────────────────
// Shows the storage architecture clearly:
//   VPS = primary binary store (active)
//   Supabase = metadata only (active, tracking URLs/sizes/types)

export default function VpsSettingsTab() {
  return (
    <div className="space-y-6 max-w-4xl">

      {/* ── Top callout ── */}
      <div className="flex items-start gap-4 px-5 py-4 bg-[#0f172a] rounded-2xl">
        <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl shrink-0">
          <i className="ri-server-fill text-white text-lg"></i>
        </div>
        <div className="flex-1 space-y-1">
          <p className="text-white text-sm font-bold">Storage Provider: VPS (Self-Hosted)</p>
          <p className="text-white/50 text-xs leading-relaxed">
            All file binaries (photos, videos, ZIPs) are stored exclusively on the VPS infrastructure.
            Supabase is used only to track file metadata — URLs, sizes, types, ownership.
            No binary data passes through or is stored in Supabase.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-400/20 border border-emerald-400/30 text-emerald-300 text-[11px] font-semibold whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
            VPS: Active
          </span>
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-400/20 border border-emerald-400/30 text-emerald-300 text-[11px] font-semibold whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
            Metadata: Active
          </span>
        </div>
      </div>

      {/* ── Two-column: VPS role vs Supabase role ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* VPS column */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 bg-[#0f172a]/3">
            <span className="w-8 h-8 flex items-center justify-center bg-[#0f172a] rounded-lg shrink-0">
              <i className="ri-server-fill text-white text-sm"></i>
            </span>
            <div>
              <p className="text-[#0f172a] text-sm font-bold">VPS Storage</p>
              <p className="text-slate-400 text-xs">Primary binary store · Self-hosted</p>
            </div>
            <span className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              Active
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {[
              { icon: 'ri-image-fill',       label: 'Stores',        value: 'Photos, videos, ZIPs, albums', cls: 'text-slate-600' },
              { icon: 'ri-folder-3-line',    label: 'Path pattern',  value: '/storage/{tenant_slug}/YYYY/MM/', cls: 'text-slate-500 font-mono text-[11px]' },
              { icon: 'ri-shield-keyhole-line', label: 'Auth',       value: 'SSH key (root restricted)', cls: 'text-slate-600' },
              { icon: 'ri-link-m',           label: 'CDN URL',       value: 'Public URL generated by VPS, stored in Supabase', cls: 'text-slate-600' },
              { icon: 'ri-upload-cloud-line', label: 'Upload flow',  value: 'Edge Function → VPS write → CDN URL → metadata INSERT', cls: 'text-slate-600' },
              { icon: 'ri-delete-bin-line',  label: 'Delete flow',   value: 'Edge Function → VPS delete → metadata DELETE', cls: 'text-slate-600' },
              { icon: 'ri-scales-line',      label: 'Quota source',  value: 'saas_tenants.storage_used_mb auto-recalc on upload/delete', cls: 'text-slate-600 font-mono text-[11px]' },
            ].map((row) => (
              <div key={row.label} className="px-5 py-3 flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className={`${row.icon} text-slate-300 text-sm`}></i>
                </span>
                <span className="text-slate-400 text-xs w-24 shrink-0 pt-0.5">{row.label}</span>
                <span className={`text-xs leading-relaxed ${row.cls}`}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Supabase column */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 bg-emerald-50/50">
            <span className="w-8 h-8 flex items-center justify-center bg-emerald-500 rounded-lg shrink-0">
              <i className="ri-database-2-line text-white text-sm"></i>
            </span>
            <div>
              <p className="text-[#0f172a] text-sm font-bold">Supabase Role</p>
              <p className="text-slate-400 text-xs">Metadata only · No binaries</p>
            </div>
            <span className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              Active
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {[
              { icon: 'ri-table-line',       label: 'Table',         value: 'uploaded_files', cls: 'text-slate-600 font-mono text-[11px]' },
              { icon: 'ri-forbid-2-line',    label: 'Does NOT store', value: 'Binary files, images, videos (VPS only)', cls: 'text-red-500' },
              { icon: 'ri-check-line',       label: 'Stores',         value: 'file_url, thumbnail_url, file_size_bytes, mime_type, entity_type', cls: 'text-slate-600 font-mono text-[11px]' },
              { icon: 'ri-shield-check-line', label: 'RLS',          value: 'tenant_id check on all rows — cross-tenant access blocked', cls: 'text-slate-600' },
              { icon: 'ri-dashboard-line',   label: 'Quota tracking', value: 'saas_tenants.storage_used_mb + storage_quota_mb', cls: 'text-slate-600 font-mono text-[11px]' },
              { icon: 'ri-time-line',        label: 'Sync',           value: 'Auto-recalc on every upload/delete via Edge Function', cls: 'text-slate-600' },
              { icon: 'ri-search-line',      label: 'Querying',       value: 'SA can browse all file metadata via /sa/storage → Files tab', cls: 'text-slate-600' },
            ].map((row) => (
              <div key={row.label} className="px-5 py-3 flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className={`${row.icon} text-slate-300 text-sm`}></i>
                </span>
                <span className="text-slate-400 text-xs w-28 shrink-0 pt-0.5">{row.label}</span>
                <span className={`text-xs leading-relaxed ${row.cls}`}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Upload flow diagram ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="text-[#0f172a] text-sm font-semibold">Upload Flow (Active)</p>
          <p className="text-slate-400 text-xs mt-0.5">How a file travels from the admin panel to the VPS</p>
        </div>
        <div className="px-5 py-6">
          <div className="flex items-center flex-wrap gap-2">
            {[
              { icon: 'ri-computer-line',         label: 'Admin UI',           sub: 'Select file',       cls: 'bg-slate-100 text-slate-600' },
              null,
              { icon: 'ri-function-line',          label: 'Edge Function',      sub: 'vps-upload-proxy', cls: 'bg-slate-100 text-slate-600' },
              null,
              { icon: 'ri-server-fill',            label: 'VPS (Binary)',       sub: 'File write',      cls: 'bg-[#0f172a] text-white' },
              null,
              { icon: 'ri-link-m',                 label: 'CDN URL',            sub: 'Public URL returned', cls: 'bg-emerald-50 text-emerald-700' },
              null,
              { icon: 'ri-database-2-line',        label: 'Supabase INSERT',    sub: 'uploaded_files row',      cls: 'bg-emerald-50 text-emerald-700' },
              null,
              { icon: 'ri-calculator-line',         label: 'Recalc',             sub: 'storage_used_mb', cls: 'bg-emerald-50 text-emerald-700' },
            ].map((step, i) =>
              step === null ? (
                <i key={i} className="ri-arrow-right-line text-slate-300 text-lg flex-shrink-0"></i>
              ) : (
                <div key={step.label} className="flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-xl border border-slate-200">
                  <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${step.cls}`}>
                    <i className={`${step.icon} text-sm`}></i>
                  </span>
                  <p className="text-slate-700 text-[11px] font-semibold whitespace-nowrap">{step.label}</p>
                  <p className="text-slate-400 text-[10px] whitespace-nowrap">{step.sub}</p>
                </div>
              )
            )}
          </div>
          <div className="mt-4 flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl">
            <i className="ri-check-double-line text-emerald-500 text-sm shrink-0"></i>
            <p className="text-emerald-700 text-xs">
              <strong>Status:</strong> Upload flow is fully operational. Quota enforcement is enforced before upload.
              Storage usage is auto-recalculated on every upload and delete.
            </p>
          </div>
        </div>
      </div>

      {/* ── Implementation checklist ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="text-[#0f172a] text-sm font-semibold">Implementation Roadmap</p>
        </div>
        <div className="px-5 py-5">
          <div className="relative">
            <div className="absolute left-3.5 top-3 bottom-3 w-px bg-slate-200"></div>
            <div className="space-y-4">
              {[
                { done: true,  label: 'uploaded_files metadata table',        note: 'Tracks URLs, sizes, types, tenant ownership — active' },
                { done: true,  label: 'storage_quota_mb per tenant',          note: 'Quota allocation editable via SA panel' },
                { done: true,  label: 'storage_used_mb tracking column',      note: 'Auto-recalc on upload/delete via Edge Function' },
                { done: true,  label: 'SA Storage Center dashboard',          note: 'Overview, per-tenant, files browse, architecture' },
                { done: true,  label: 'VPS provisioning & SFTP setup',        note: 'Dedicated server for heavy file storage — active' },
                { done: true,  label: 'Upload Edge Function',                  note: 'Receives multipart, writes to VPS, returns CDN URL, recalc usage' },
                { done: true,  label: 'Delete Edge Function',                  note: 'SFTP delete + metadata row cleanup + recalc usage' },
                { done: true,  label: 'Quota enforcement',                     note: 'Blocks upload when tenant exceeds quota' },
                { done: true,  label: 'Admin file manager UI',                note: 'Browse, preview, delete files from admin panel' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4 pl-9 relative">
                  <span className={`absolute left-0 w-7 h-7 flex items-center justify-center rounded-full border-2 text-xs font-bold ${
                    item.done
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : 'bg-white border-slate-300 text-slate-300'
                  }`}>
                    {item.done ? <i className="ri-check-line text-xs"></i> : <i className="ri-time-line text-xs"></i>}
                  </span>
                  <div>
                    <p className={`text-sm font-semibold ${item.done ? 'text-slate-700' : 'text-slate-400'}`}>{item.label}</p>
                    <p className="text-slate-400 text-xs mt-0.5">{item.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}